import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import { axiosInstance } from "@retro-ia/shared/axios";

export type AlcanceRetroPdf = "historial" | "ultimo";

interface DescargarRetroPdfParams {
  idActividadAprendiz: number;
  alcance: AlcanceRetroPdf;
  actividadNombre: string;
}

const slug = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase() || "actividad";

/** Con responseType "blob" el mensaje de error del backend llega como Blob. */
async function mensajeDeError(error: unknown): Promise<string | null> {
  if (isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      const data = JSON.parse(await error.response.data.text());
      return typeof data?.message === "string" ? data.message : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Descarga en PDF la retroalimentación de una actividad: el historial
 * completo (todos los intentos y la conversación con el coach) o solo el
 * último intento.
 */
export function useDescargarRetroPdf() {
  return useMutation({
    mutationFn: async ({
      idActividadAprendiz,
      alcance,
      actividadNombre,
    }: DescargarRetroPdfParams) => {
      const res = await axiosInstance.get<Blob>(
        `/reportes/actividad-aprendiz/${idActividadAprendiz}/historial/pdf`,
        { params: { alcance }, responseType: "blob" },
      );
      const prefijo =
        alcance === "historial" ? "historial-retroalimentacion" : "retroalimentacion";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${prefijo}-${slug(actividadNombre)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onError: async (error) => {
      toast.error("No se pudo descargar el PDF", {
        description:
          (await mensajeDeError(error)) ??
          "Revisa tu conexión e inténtalo de nuevo.",
      });
    },
  });
}
